import { IParkingModel, UserService } from '@aggregator/core';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { LocalizationPipe } from '@shared/modules/localization';
import { MessagesService } from '@shared/modules/messages';
import {
  FormBuilderService,
  IFormGroupConfig,
  ITableActionsColumnReturnData,
  ITableConfig,
  UIButton,
  UIFilter,
  UIFormModule,
  UIIcon,
  UIModalService,
  UIPageContentLayout,
  UITable,
} from '@shared/ui';
import { catchError } from 'rxjs';
import { OperatorParkingDetailsModal } from './components';
import { filterFormConfig, tableConfig } from './configs';
import { ParkingService } from './services';

@Component({
  selector: 'aggregator-operator-parking-page',
  templateUrl: './operator-parking.component.html',
  styleUrl: './operator-parking.component.scss',
  standalone: true,
  imports: [UIPageContentLayout, UITable, UIFormModule, UIFilter, UIButton, UIIcon],
  providers: [ParkingService, LocalizationPipe],
})
export class OperatorParkingPage implements OnInit {
  isImporting = false;

  isFilterVisible = false;
  loading = true;
  total = 0;
  pageSize = 20;
  pageIndex = 1;
  listData: IParkingModel[] = [];

  tableConfig: ITableConfig = tableConfig;

  filterForm?: FormGroup;
  filterFormConfig: IFormGroupConfig = filterFormConfig;

  get operatorId(): string | undefined {
    return this.userService.userInfo?.operatorId;
  }

  get filterValue(): { startDate: string; endDate: string } {
    return this.filterForm?.getRawValue();
  }

  constructor(
    private formBuilder: FormBuilderService,
    private modalService: UIModalService,
    private service: ParkingService,
    private destroy$: DestroyRef,
    private cdr: ChangeDetectorRef,
    private messagesService: MessagesService,
    private localizationPipe: LocalizationPipe,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.filterForm = this.formBuilder.build(this.filterFormConfig);
  }

  applyFilter(): void {
    this.pageIndex = 1;
    this.onFilterCollapsed();
    this.loadDataFromServer();
  }

  onFilterCollapsed(): void {
    this.isFilterVisible = !this.isFilterVisible;
    this.cdr.detectChanges();
  }

  loadDataFromServer(): void {
    this.loading = true;

    this.service
      .getAll({
        operatorId: this.operatorId,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe(repsponse => {
        this.listData = repsponse?.value?.items ?? [];
        this.total = repsponse?.value?.total ?? 0;
        this.loading = false;
      });
  }

  onOpenDetailsModal(id?: string): void {
    this.modalService.create(OperatorParkingDetailsModal, id).subscribe(isSuccess => {
      if (isSuccess) {
        this.loadDataFromServer();
      }
    });
  }

  onDeleteItem(data?: IParkingModel): void {
    if (!data?.id) {
      return;
    }
    this.modalService
      .confirm({
        title: 'Вы уверены?',
        description: `Хотите удалить "${this.localizationPipe.transform(data?.name)}"?`,
        buttonColor: 'danger',
        onOk: () => this.service.delete(data?.id),
      })
      .subscribe(isConfirm => {
        if (isConfirm) {
          this.messagesService.showSuccess('Успешно удален!');
          this.loadDataFromServer();
        }
      });
  }

  onTableActionClicked(data: ITableActionsColumnReturnData): void {
    if (data?.type === 'details') {
      this.onOpenDetailsModal(data?.rowData?.id);
    }
    if (data.type === 'delete') {
      this.onDeleteItem(data?.rowData);
    }
  }

  onImport(files: File[]): void {
    if (this.isImporting) {
      return;
    }

    this.isImporting = true;
    if (!files || files.length === 0) {
      return;
    }

    const formData = new FormData();
    formData.append('File', files[0]);

    this.service
      .upload(formData)
      .pipe(
        catchError(error => {
          this.isImporting = false;
          throw error;
        }),
        takeUntilDestroyed(this.destroy$),
      )
      .subscribe(response => {
        this.isImporting = false;

        if (response.isSuccess) {
          this.loadDataFromServer();
        } else {
          // TODO: Check Implemation of error handling
          const firstError = Array.isArray(response.errors) ? response.errors[0] : response.errors;

          throw firstError;
        }
      });
  }
}
